import { Space, Typography, Button, Popconfirm, Alert } from "antd";
import { ColumnsType } from "antd/es/table";
import { useState, useEffect } from "react";
import {
  useLoaderData,
  useNavigation,
  useSubmit,
  useFetchers,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as APITypes from "~/API";
import {
  CustomTextField,
  AccessRightStatusTag,
  CustomTable,
  TableHeaderBar,
} from "~/components";
import { dateToLocalTime, INTENTS } from "~/utils";
import type { FormattedResourceAccessRight } from "~/types";
import type { InstanceAccessTabLoaderType } from "../../loaders";
import { AccessStatusDropdown } from "../AccessStatusDropdown";
import { GrantAccessDrawer } from "./GrantAccessDrawer";
import { OperationLoadingBanner } from "./OperationLoadingBanner";

type InstanceAccessRow = {
  key: string;
  resourceID: string;
  resourceType: APITypes.EntityType;
  readAccessStatus: APITypes.ResourceAccessRightStatus;
  writeAccessStatus: APITypes.ResourceAccessRightStatus;
  grantedDate: string;
  lastModified: string;
  accessRight: FormattedResourceAccessRight;
};

const getInstanceAccessColumns = ({
  t,
  isLoading,
  handleUpdateAccessStatus,
  handleRemoveAccess,
  getFormattedResourceName,
  getResourceLink,
  canUpdateMemberRights,
}: {
  t: any;
  isLoading: boolean;
  handleUpdateAccessStatus: (
    record: InstanceAccessRow,
    readStatus?: APITypes.ResourceAccessRightStatus,
    writeStatus?: APITypes.ResourceAccessRightStatus
  ) => void;
  handleRemoveAccess: (record: InstanceAccessRow) => void;
  getFormattedResourceName: (record: InstanceAccessRow) => string;
  getResourceLink: (record: InstanceAccessRow) => string;
  canUpdateMemberRights: boolean;
}): ColumnsType<InstanceAccessRow> => [
  {
    title: t("Resource"),
    key: "resource",
    width: 300,
    render: (_, record) => (
      <CustomTextField
        value={getFormattedResourceName(record)}
        link={getResourceLink(record)}
        newPage={true}
        style={{ fontWeight: 600 }}
      />
    ),
  },
  {
    title: t("Read"),
    dataIndex: "readAccessStatus",
    key: "readAccessStatus",
    width: 150,
    align: "center",
    render: (status: APITypes.ResourceAccessRightStatus) => (
      <AccessRightStatusTag status={status} />
    ),
  },
  {
    title: t("Write"),
    dataIndex: "writeAccessStatus",
    key: "writeAccessStatus",
    width: 150,
    align: "center",
    render: (status: APITypes.ResourceAccessRightStatus) => (
      <AccessRightStatusTag status={status} />
    ),
  },
  {
    title: t("Granted_Date"),
    dataIndex: "grantedDate",
    key: "grantedDate",
    width: 180,
    render: (date: string) => (date ? dateToLocalTime(date, "dateTime") : "-"),
  },
  {
    title: t("Last_Modified"),
    dataIndex: "lastModified",
    key: "lastModified",
    width: 180,
    render: (date: string) => (date ? dateToLocalTime(date, "dateTime") : "-"),
  },
  {
    title: t("Actions"),
    key: "actions",
    width: 250,
    render: (_, record) => (
      <Space direction="horizontal" size="small" style={{ width: "100%" }}>
        {/* Read Access Actions */}
        <AccessStatusDropdown
          currentStatus={record.readAccessStatus}
          onChange={(newStatus) =>
            handleUpdateAccessStatus(
              record,
              newStatus,
              record.writeAccessStatus
            )
          }
          label={t("Read")}
          isLoading={isLoading}
          isAdmin={canUpdateMemberRights}
        />

        {/* Write Access Actions */}
        <AccessStatusDropdown
          currentStatus={record.writeAccessStatus}
          onChange={(newStatus) =>
            handleUpdateAccessStatus(record, record.readAccessStatus, newStatus)
          }
          label={t("Write")}
          isLoading={isLoading}
          isAdmin={canUpdateMemberRights}
        />

        {/* Remove Button */}
        <Popconfirm
          title={t("Remove_Access_Confirm")}
          onConfirm={() => handleRemoveAccess(record)}
          okText={t("Confirm")}
          cancelText={t("Cancel")}
          disabled={isLoading}
        >
          <Button size="small" danger disabled={isLoading} block>
            {t("Remove")}
          </Button>
        </Popconfirm>
      </Space>
    ),
  },
];

export const InstanceAccessTab = () => {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as InstanceAccessTabLoaderType;
  const navigation = useNavigation();
  const submit = useSubmit();
  const fetchers = useFetchers();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isDrawerSubmitting, setIsDrawerSubmitting] = useState(false);

  // Check if any fetcher is submitting (from drawer or inline actions)
  const isFetcherSubmitting = fetchers.some(
    (fetcher) => fetcher.state === "submitting" || fetcher.state === "loading"
  );

  const isSubmitting =
    navigation.state === "submitting" ||
    isFetcherSubmitting ||
    isDrawerSubmitting;
  const isLoading = isSubmitting;

  // Debug logging
  console.log("InstanceAccessTab - Debug Info:", {
    navigationState: navigation.state,
    fetchersCount: fetchers.length,
    fetchersStates: fetchers.map((f) => f.state),
    isFetcherSubmitting,
    isDrawerSubmitting,
    isLoading,
    isSubmitting,
  });

  // Reset drawer submitting state when loading completes
  useEffect(() => {
    if (
      isDrawerSubmitting &&
      navigation.state === "idle" &&
      !isFetcherSubmitting
    ) {
      setIsDrawerSubmitting(false);
    }
  }, [isDrawerSubmitting, navigation.state, isFetcherSubmitting]);

  // Transform access rights to table data
  const tableData: InstanceAccessRow[] = loaderData.accessRights.map(
    (right) => ({
      key: right.resourceID || "",
      resourceID: right.resourceID || "",
      resourceType: right.resourceType || APITypes.EntityType.Property,
      readAccessStatus:
        right.readAccessStatus || APITypes.ResourceAccessRightStatus.NotGranted,
      writeAccessStatus:
        right.writeAccessStatus ||
        APITypes.ResourceAccessRightStatus.NotGranted,
      grantedDate: right.createdAt || "",
      lastModified: right.updatedAt || "",
      accessRight: right,
    })
  );

  const handleRemoveAccess = (record: InstanceAccessRow) => {
    const formData = new FormData();
    formData.append("intent", INTENTS._remove_instance_access);
    formData.append(
      "body",
      JSON.stringify({
        resourceID: record.resourceID,
        _version: record.accessRight._version,
      })
    );
    submit(formData, { method: "post" });
  };

  const handleUpdateAccessStatus = (
    record: InstanceAccessRow,
    readStatus?: APITypes.ResourceAccessRightStatus,
    writeStatus?: APITypes.ResourceAccessRightStatus
  ) => {
    const formData = new FormData();
    formData.append("intent", INTENTS._edit_instance_access);
    formData.append(
      "body",
      JSON.stringify({
        id: record.accessRight.id,
        readAccessStatus:
          readStatus !== undefined ? readStatus : record.readAccessStatus,
        writeAccessStatus:
          writeStatus !== undefined ? writeStatus : record.writeAccessStatus,
        _version: record.accessRight._version,
      })
    );
    submit(formData, { method: "post" });
  };

  const getResourceTypeLabel = (resourceType: APITypes.EntityType) => {
    return t(resourceType as any) || resourceType;
  };

  const getFormattedResourceName = (record: InstanceAccessRow) => {
    const resourceType = getResourceTypeLabel(record.resourceType);

    if (record.accessRight.resourceDetails?.property) {
      return `${resourceType}: ${record.accessRight.resourceDetails.property.name}`;
    } else if (record.accessRight.resourceDetails?.building) {
      return `${resourceType}: ${record.accessRight.resourceDetails.building.name}`;
    } else if (record.accessRight.resourceDetails?.unit) {
      return `${resourceType}: ${record.accessRight.resourceDetails.unit.name}`;
    } else if (record.accessRight.resourceDetails?.serviceRequest) {
      return `${resourceType}: ${record.accessRight.resourceDetails.serviceRequest.title}`;
    } else if (record.accessRight.resourceDetails?.amenity) {
      return `${resourceType}: ${record.accessRight.resourceDetails.amenity.name}`;
    }

    // Fallback to ID if resource details not found
    return `${resourceType}: ${record.resourceID}`;
  };

  const getResourceLink = (record: InstanceAccessRow): string => {
    const resourceType = record.resourceType.toLowerCase();
    const resourceID = record.resourceID;
    return `/${resourceType}/${resourceID}`;
  };

  const columns = getInstanceAccessColumns({
    t,
    isLoading,
    handleUpdateAccessStatus,
    handleRemoveAccess,
    getFormattedResourceName,
    getResourceLink,
    canUpdateMemberRights: loaderData.canUpdateMemberRights,
  });

  // If user is Admin or SuperAdmin, show message instead of table
  if (loaderData.isAdminOrSuperAdmin) {
    return (
      <div>
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Typography.Title level={5}>{t("Instance_Access")}</Typography.Title>
          <Typography.Text type="secondary">
            {t("Instance_Access_Description")}
          </Typography.Text>
        </Space>

        <Alert
          message={t("Admin_Auto_Access_Title")}
          description={t("Admin_Auto_Access_Description")}
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <>
      {/* Show loading banner when form is being submitted */}
      {isSubmitting && <OperationLoadingBanner preset="instanceAccess" />}

      <CustomTable
        tableHeaders={
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Typography.Title level={5}>
                {t("Instance_Access")}
              </Typography.Title>
              <Typography.Text type="secondary">
                {t("Instance_Access_Description")}
              </Typography.Text>
            </div>

            <TableHeaderBar
              left={
                <Typography.Text>
                  <strong>
                    {tableData.length} {t("Total_Records_Found")}
                  </strong>
                </Typography.Text>
              }
              right={
                <Button
                  type="primary"
                  onClick={() => setDrawerVisible(true)}
                  disabled={isLoading}
                >
                  {loaderData.canUpdateMemberRights
                    ? t("Grant_New_Access")
                    : t("Request_New_Access")}
                </Button>
              }
            />
          </Space>
        }
        tableProps={{
          columns,
          dataSource: tableData,
          loading: isLoading,
          pagination: { pageSize: 10 },
        }}
      />

      <GrantAccessDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        memberID={loaderData.memberID}
        organizationID={loaderData.organizationID}
        existingAccessRights={loaderData.accessRights}
        isAdmin={loaderData.canUpdateMemberRights}
        onSubmitStart={() => setIsDrawerSubmitting(true)}
      />
    </>
  );
};
